// @flow

import React from 'react';
import { orderBy } from 'lodash';

import { AppIntegrationDetails } from '@zapier/design-system-app-profiles';
import { Screen } from '@zapier/theme';
import { useMediaProps } from '@zapier/common-components';

import ApolloQuery from 'web-ssr/app/common/components/ApolloQuery';
import WithAuthentication from 'web-ssr/app/common/components/WithAuthentication';
import { buildSignUpUrl } from 'web-ssr/app/common/utils/AppUtils';
import { createZapLink } from 'web-ssr/app/services/utils';

import appIntegrationDetailsQuery from './appIntegrationDetailsQuery';

import type {
  appIntegrationDetailsQuery as appIntegrationDetailsQueryType,
  appIntegrationDetailsQueryVariables,
} from 'web-ssr/graphql/types';

type Result = {
  loading: *,
  fetchMore: *,
  error: *,
  data: appIntegrationDetailsQueryType,
};

type Props = {
  primaryAppSlug: string,
};

// Using Object type here to bypass a heinous Flow error which defies all logic.
// https://cdn.zapier.com/storage/photos/ca5d5d7c1f6dcad90bc17be81c1aab1a.png
const filterHiddenActions = (actions: Array<Object>) => {
  if (!actions) {
    return [];
  }

  return actions.filter(action => !action.hide);
};

// Using Object type here to bypass a heinous Flow error which defies all logic.
// https://cdn.zapier.com/storage/photos/d24754fad0bd2c00c17d31d469dffb81.png
const transformActions = (actions: Array<Object>) =>
  actions.map(action => ({
    name: action.label,
    descriptionHtmlAst: action.helpTextHtmlAst,
  }));

const AppIntegrationDetailsContainer = (props: Props) => {
  const { primaryAppSlug } = props;

  const layoutProps = useMediaProps({
    [Screen.queries.phone]: { size: 'small' },
    [Screen.queries.phablet]: { size: 'small' },
    [Screen.queries.tablet]: { size: 'medium' },
    [Screen.queries.desktop]: { size: 'large' },
  });

  const variables: appIntegrationDetailsQueryVariables = {
    slug: primaryAppSlug,
  };

  return (
    <WithAuthentication>
      {({ isAuthenticated }) => (
        <ApolloQuery query={appIntegrationDetailsQuery} variables={variables}>
          {(result: Result) => {
            const { data, error } = result;

            const { appBySlug: app } = data;

            if (error || !app) {
              return null;
            }

            const { currentImplementation, integrationOverviewHtmlAst } = app;

            if (
              !currentImplementation ||
              !currentImplementation.actions ||
              !integrationOverviewHtmlAst
            ) {
              return null;
            }

            const { reads, writes, searches } = currentImplementation.actions;

            const visibleTriggers = orderBy(
              filterHiddenActions(reads),
              ['important'],
              ['desc']
            );

            const visibleSearches = orderBy(
              filterHiddenActions(searches),
              ['important'],
              ['desc']
            );

            const visibleActions = orderBy(
              filterHiddenActions(writes),
              ['important'],
              ['desc']
            );

            // Only show the module if there are actions to display!
            if (
              !visibleTriggers.length &&
              !visibleSearches.length &&
              !visibleActions.length
            ) {
              return null;
            }

            const createZapUrl = createZapLink(currentImplementation.id);
            const callToActionUrl = !isAuthenticated
              ? buildSignUpUrl({
                  next: createZapUrl,
                  serviceIds: [currentImplementation.id],
                })
              : createZapUrl;

            return (
              <AppIntegrationDetails
                actions={transformActions(visibleActions)}
                appIconUrl={app.image || '#'}
                appName={app.name}
                callToActionUrl={callToActionUrl}
                integrationOverviewHtmlAst={integrationOverviewHtmlAst}
                launchDate={new Date(app.launchDate)}
                searches={transformActions(visibleSearches)}
                size={layoutProps.size}
                triggers={transformActions(visibleTriggers)}
              />
            );
          }}
        </ApolloQuery>
      )}
    </WithAuthentication>
  );
};

export default AppIntegrationDetailsContainer;
